import { LitElement, html } from 'lit'
import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
import { contaCacheService } from '../core/services/conta-cache.service.js'

/**
 * Modal para criar e editar contas bancárias
 * Suporta diferentes tipos: Conta Corrente, Poupança, Cartão de Crédito, Investimento
 * Cartões de crédito podem ter conta associada para débito da fatura
 * Calcula saldo atual automaticamente em modo de edição
 *
 * Utiliza ModalSaveMixin para:
 * - Loading no botão Salvar com guard contra clique duplo
 * - Chamada direta ao contaCacheService (criar/atualizar)
 * - Exibição de erro inline com data-cy="erro-salvar-conta"
 * - Guard de fechamento durante salvamento (_saving)
 * - Eventos cache-operation-started/finished via window
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalConta extends ModalSaveMixin(LitElement) {
  static properties = {
    isEditMode: { type: Boolean },
    accountData: { type: Object },
    saldoAtualCalculado: { type: Number },
    selectedType: { type: String },
    contas: { type: Array }
  }

  /**
   * Identificador da entidade para o ModalSaveMixin.
   * Usado nos eventos cache-operation-started/finished e no data-cy de erro inline.
   * @type {string}
   */
  _entidade = 'conta'

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this.open = false
    this.isEditMode = false
    this.accountData = null
    this.saldoAtualCalculado = 0
    this.selectedType = 'Conta Corrente'
    this.contas = []
  }

  async show(accountData = null) {
    this.isEditMode = !!accountData
    this.accountData = accountData
    this.open = true
    
    await this._loadContas()
    
    if (this.isEditMode && accountData) {
      setTimeout(() => {
        const nameInput = this.renderRoot.querySelector('[data-cy="input-nome"]')
        const balanceInput = this.renderRoot.querySelector('[data-cy="input-saldo"]')
        const typeSelect = this.renderRoot.querySelector('[data-cy="select-tipo"]')
        
        if (nameInput) nameInput.value = accountData.nome || accountData.name || ''
        if (balanceInput) {
          const saldo = accountData.saldoInicial || accountData.balance || 0
          balanceInput.value = Math.abs(saldo).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          })
          balanceInput.addEventListener('blur', (e) => this._calcularSaldoAtual(e))
        }
        if (typeSelect) {
          typeSelect.value = accountData.tipo || accountData.type || 'Conta Corrente'
          typeSelect.addEventListener('change', (e) => this._onTypeChange(e))
        }
        
        const contaAssociadaSelect = this.renderRoot.querySelector('[data-cy="select-conta-associada"]')
        if (contaAssociadaSelect && accountData.contaAssociada) {
          contaAssociadaSelect.value = accountData.contaAssociada
        }
        
        this.selectedType = accountData.tipo || accountData.type || 'Conta Corrente'
        this.saldoAtualCalculado = accountData.saldoAtual || 0
      }, 0)
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Delega para this.close() que já gerencia o estado do modal
   * e aplica guard contra fechamento durante salvamento (_saving)
   */
  _handleBackdropClick() {
    this.close()
  }

  _stopPropagation(e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar/Atualizar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover(e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar/Atualizar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave(e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover(e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave(e) {
    e.target.style.backgroundColor = ''
  }

  _formatCurrency(value) {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  /**
   * Formata o valor digitado no campo de saldo como moeda BRL.
   * Remove caracteres não numéricos, divide por 100 e formata.
   * Mesmo padrão do modal de lançamentos.
   * @param {Event} e - Evento de input
   * @private
   */
  _formatCurrencyInput (e) {
    let value = e.target.value.replace(/\D/g, '')
    if (value === '') {
      e.target.value = ''
      return
    }
    const numericValue = parseInt(value) / 100
    e.target.value = numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  /**
   * Extrai valor numérico de um campo formatado em BRL.
   * @param {HTMLElement} input - Elemento input com valor formatado
   * @returns {number} Valor numérico (0 se vazio)
   * @private
   */
  _getValorFromInput (input) {
    if (!input) return 0
    const value = input.value || ''
    const numericValue = value.replace(/[^\d,]/g, '').replace(',', '.')
    return parseFloat(numericValue || 0)
  }

  async _loadContas() {
    try {
      // Usa cache inteligente para contas
      this.contas = await contaCacheService.listar()
      this.requestUpdate()
    } catch (error) {
      console.error('Erro ao carregar contas:', error)
    }
  }

  _onTypeChange(e) {
    this.selectedType = e.target.value
  }

  _calcularSaldoAtual(e) {
    if (!this.isEditMode || !this.accountData) return
    
    const novoSaldoInicial = this._getValorFromInput(e.target)
    const saldoInicialOriginal = this.accountData.saldoInicial || 0
    const saldoAtualOriginal = this.accountData.saldoAtual || 0
    
    const diferenca = novoSaldoInicial - saldoInicialOriginal
    this.saldoAtualCalculado = saldoAtualOriginal + diferenca
  }

  /**
   * Executa a operação de salvamento via contaCacheService.
   * Coleta dados do formulário, determina operação (criar/atualizar)
   * e delega ao _executeSave do ModalSaveMixin que gerencia:
   * - Guard contra clique duplo (_saving)
   * - Loading no botão Salvar
   * - Timeout de 30s
   * - Tratamento de erro inline
   * - Eventos cache-operation-started/finished
   * - Fechamento do modal após sucesso
   *
   * @returns {Promise<void>}
   */
  async _handleSave() {
    const nameInput = this.renderRoot.querySelector('[data-cy="input-nome"]')
    const balanceInput = this.renderRoot.querySelector('[data-cy="input-saldo"]')
    const typeSelect = this.renderRoot.querySelector('[data-cy="select-tipo"]')
    const contaAssociadaSelect = this.renderRoot.querySelector('[data-cy="select-conta-associada"]')
    
    const dados = {
      nome: nameInput.value,
      tipo: typeSelect.value
    }
    
    if (typeSelect.value === 'Cartão de Crédito' && contaAssociadaSelect) {
      dados.contaAssociada = contaAssociadaSelect.value || null
    }
    
    if (this.isEditMode) {
      dados.saldoInicial = this._getValorFromInput(balanceInput)
      dados.saldoAtual = this.saldoAtualCalculado
    } else {
      dados.saldoInicial = this._getValorFromInput(balanceInput)
    }

    const operacao = this.isEditMode ? 'atualizar' : 'criar'
    const id = this.isEditMode ? this.accountData?.id : undefined

    await this._executeSave(contaCacheService, operacao, dados, id)
  }

  render() {
    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 500px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            position: relative;
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-conta-title">
              ${this.isEditMode ? 'Editar Conta' : 'Adicionar Conta'}
            </p>
            ${this.renderBotaoFechar('modal-conta-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          " @input="${this._clearError}" @change="${this._clearError}">
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Nome</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="Ex: Conta Itaú"
                  required
                  data-cy="input-nome"
                />
              </div>
            </div>

            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Saldo Inicial</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="R$ 0,00"
                  data-cy="input-saldo"
                  @input="${this._formatCurrencyInput}"
                />
              </div>
            </div>

            ${this.isEditMode ? html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Saldo Atual</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    readonly
                    .value="${this._formatCurrency(this.saldoAtualCalculado)}"
                    data-cy="input-saldo-atual"
                  />
                </div>
                <p class="help">Calculado automaticamente pelas transações</p>
              </div>
            ` : ''}

            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Tipo</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select data-cy="select-tipo" @change="${this._onTypeChange}">
                    <option value="Conta Corrente">Conta Corrente</option>
                    <option value="Poupança">Poupança</option>
                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                    <option value="Investimento">Investimento</option>
                  </select>
                </div>
              </div>
            </div>

            ${this.selectedType === 'Cartão de Crédito' ? html`
              <div class="field mb-4">
                <label class="label is-size-7 has-text-weight-medium">Conta Associada</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select data-cy="select-conta-associada">
                      <option value="">Nenhuma (opcional)</option>
                      ${this.contas
                        .filter(conta => conta.tipo !== 'Cartão de Crédito' && conta.id !== this.accountData?.id)
                        .map(conta => html`
                          <option value="${conta.id}">${conta.nome}</option>
                        `)}
                    </select>
                  </div>
                </div>
                <p class="help">Selecione a conta onde as faturas serão debitadas</p>
              </div>
            ` : ''}

            <!-- Mensagem de erro inline posicionada abaixo dos campos e acima do footer -->
            ${this.renderErroSalvar()}
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-conta"
            aria-label="Ações do modal de conta"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-cancelar-conta"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-conta"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  ${this.isEditMode ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    `
  }
}

customElements.define('modal-conta', ModalConta)
